import { useEffect, useState } from "react";
import { Plus, Trash2, Lock, Pencil } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmModal";

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ts; }
}

/**
 * NotesTimeline
 * Polymorphic notes timeline (lead | contact | deal).
 *   • Default mode (`readOnly` = false): lists notes for `linked_type` + `linked_id`,
 *     "Add Note" opens a side-panel form, click row → side panel.
 *       - Author: edit / save / delete
 *       - Others: read-only
 *   • `readOnly` + `source`: fetches the aggregated feed from `source` (e.g.
 *     `/contacts/{id}/notes`) and hides the "Add Note" button. Rows still open
 *     read-only — the note's `linked_type` is shown for context.
 */
export default function NotesTimeline({ linkedType, linkedId, readOnly = false, source }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [openNote, setOpenNote] = useState(null);

  const load = async () => {
    try {
      const url = source || `/notes?linked_type=${linkedType}&linked_id=${linkedId}`;
      const { data } = await api.get(url);
      setNotes(data);
    } catch { toast.error("Failed to load notes"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [linkedType, linkedId, source]);

  return (
    <div className="pg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm mono uppercase text-mute tracking-wider">Notes</h3>
        {!readOnly && (
          <button className="pg-btn" onClick={() => setShowNew(true)} data-testid="add-note-btn">
            <Plus size={14} /> Add Note
          </button>
        )}
      </div>

      {notes.length === 0 ? (
        <div className="text-mute mono text-xs">No notes yet</div>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => {
            const mine = !readOnly && user && n.created_by === user.id;
            return (
              <button
                key={n.id}
                onClick={() => setOpenNote(n)}
                className="w-full text-left flex items-start gap-3 p-3"
                style={{ background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)", borderRadius: "var(--r-md)" }}
                data-testid={`note-row-${n.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm flex items-center gap-2" style={{ color: "var(--pg-text)" }}>
                    <span className="truncate">{n.subject}</span>
                    {!mine && <Lock size={11} style={{ color: "var(--pg-text-mute)" }} />}
                  </div>
                  <div className="mono text-[11px] text-mute mt-1">
                    {fmt(n.created_at)} · {n.created_by_name || "—"} ·{" "}
                    {readOnly && n.linked_type
                      ? <span className="text-mute">linked to {n.linked_type} · click to read</span>
                      : (mine ? "click to edit" : "click to read")
                    }
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {readOnly && (
        <div className="mono text-[11px] text-mute mt-4" data-testid="notes-readonly-hint">
          Notes are created inside Leads or Deals.
        </div>
      )}

      {showNew && !readOnly && (
        <NoteForm
          mode="create"
          linkedType={linkedType}
          linkedId={linkedId}
          onClose={() => setShowNew(false)}
          onDone={() => { setShowNew(false); load(); }}
        />
      )}

      {openNote && (
        <NoteForm
          mode={!readOnly && user && openNote.created_by === user.id ? "edit" : "view"}
          note={openNote}
          onClose={() => setOpenNote(null)}
          onDone={() => { setOpenNote(null); load(); }}
        />
      )}
    </div>
  );
}

function NoteForm({ mode, note, linkedType, linkedId, onClose, onDone }) {
  const [subject, setSubject] = useState(note?.subject || "");
  const [body, setBody] = useState(note?.body || "");
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();
  const readOnly = mode === "view";
  const isEdit = mode === "edit";
  const isCreate = mode === "create";

  const submit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setSaving(true);
    try {
      if (isCreate) {
        await api.post("/notes", { subject, body, linked_type: linkedType, linked_id: linkedId });
        toast.success("Note added");
      } else {
        await api.patch(`/notes/${note.id}`, { subject, body });
        toast.success("Note updated");
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!await confirm({ title: "Delete this note?", message: "This cannot be undone.", destructive: true, confirmLabel: "Delete" })) return;
    try {
      await api.delete(`/notes/${note.id}`);
      toast.success("Note deleted");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const title = isCreate ? "New Note" : isEdit ? "Edit Note" : "Note";

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="note-form-panel">
        <div className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl flex items-center gap-2">
            {title}
            {readOnly && <Lock size={14} style={{ color: "var(--pg-text-mute)" }} />}
            {isEdit && <Pencil size={14} style={{ color: "var(--pg-text-mute)" }} />}
          </h2>
          {!isCreate && note && (
            <div className="mono text-[11px] text-mute">
              {fmt(note.created_at)} · {note.created_by_name || "—"}
            </div>
          )}
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Subject</label>
            <input
              className="pg-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              readOnly={readOnly}
              required
              data-testid="note-input-subject"
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Body</label>
            <textarea
              className="pg-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              readOnly={readOnly}
              rows={10}
              data-testid="note-input-body"
            />
          </div>
          {readOnly && (
            <div className="mono text-[11px] text-mute" data-testid="note-readonly-hint">
              You are viewing this note as read-only. Only the author can edit it.
            </div>
          )}
        </form>
        <div className="px-6 py-4 flex justify-between items-center gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <div>
            {isEdit && (
              <button type="button" onClick={remove} className="pg-btn pg-btn-danger" data-testid="note-delete">
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" className="pg-btn" onClick={onClose}>{readOnly ? "Close" : "Cancel"}</button>
            {!readOnly && (
              <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="note-submit">
                {saving ? "Saving…" : isCreate ? "Create Note" : "Save"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
